// The post-activation SUCCESS state (prototype activatedView): the seal, the live line, and the two
// hand-offs — open the activated definition's board, or return to the landing. The board link
// renders ONLY when the landing re-read actually listed the definition (G-182 — the hand-off id is
// resolved from the owning read, never fabricated); otherwise the honest path back is all that is
// offered. The sweep line derives from the projection's declared cadence. Tokens only (G-181).

import { Icon } from '../../../../app/Icon.tsx';
import { GHOST_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from '../composer-ui.tsx';

export interface ActivatedViewProps {
  readonly workflowName: string;
  readonly cadenceLabel: string;
  readonly timezone: string;
  /** The activated definition's board id, when the landing lists it; null hides the board link. */
  readonly boardId: string | null;
  readonly onOpenBoard: (definitionId: string) => void;
  readonly onBack: () => void;
}

/** The "workflow is live" state. */
export function ActivatedView({
  workflowName,
  cadenceLabel,
  timezone,
  boardId,
  onOpenBoard,
  onBack,
}: ActivatedViewProps): React.JSX.Element {
  return (
    <div data-activated="true" className="mx-auto mt-16 max-w-xl text-center">
      <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-forest-tint text-forest">
        <Icon name="check" size={30} />
      </span>
      <h2 className="ritona-voice text-2xl font-medium tracking-snug text-text">
        {workflowName} is live
      </h2>
      <p className="mx-auto mt-3 max-w-prose text-sm leading-relaxed text-text-muted">
        The first sweep runs on its declared cadence — {cadenceLabel} ({timezone}). It will pull the
        declared evidence, materialise items onto the board, and freeze any decisions for you. The
        next workflow follows as pure configuration.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        {boardId !== null ? (
          <button
            type="button"
            aria-label={`Open the board: ${workflowName}`}
            onClick={() => onOpenBoard(boardId)}
            className={PRIMARY_BUTTON_CLASS}
          >
            Open the board
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Back to workflows"
          onClick={onBack}
          className={GHOST_BUTTON_CLASS}
        >
          Back to workflows
        </button>
      </div>
    </div>
  );
}
